import { CalendarDays, Camera, KeyRound, Languages, Mail, PanelLeft, ShieldCheck, Trash2, UserRound, ZoomIn } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth';
import { Avatar } from '../components/Avatar';
import { AccentPicker } from '../components/AccentPicker';
import { AvatarCropper } from '../components/AvatarCropper';
import { Topbar } from '../components/Layout';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { COVERS, coverFor, type Cover } from '../components/profile';
import { Field, PasswordInput, useToast } from '../components/ui';
import { formatDate } from '../format';
import { RAIL_KEY, usePref, validZoom, ZOOM_KEY, ZOOM_LEVELS } from '../hooks/usePref';
import { useI18n, type MessageKey } from '../i18n';
import { roleLabel } from '../roles';

const TITLE_MAX = 80;
const BIO_MAX = 280;
/** Permissions shown as chips before "+N more". */
const ACCESS_SHOWN = 8;

export function AccountPage() {
  const { user, refresh } = useAuth();
  const toast = useToast();
  const { t } = useI18n();
  const [name, setName] = useState(user?.name ?? '');
  const [title, setTitle] = useState(user?.title ?? '');
  const [bio, setBio] = useState(user?.bio ?? '');
  const [cover, setCover] = useState<Cover>(user ? coverFor(user) : COVERS[0]);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [pwFields, setPwFields] = useState<Record<string, string>>({});
  const [cropping, setCropping] = useState<File | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Display preferences live in this browser; the sidebar's own button changes the same setting.
  const [zoomPref, setZoom] = usePref(ZOOM_KEY, 1);
  const zoom = validZoom(zoomPref);
  const [rail, setRail] = usePref(RAIL_KEY, false);

  // Start from the saved profile again after it is saved or changed elsewhere.
  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setTitle(user.title ?? '');
    setBio(user.bio ?? '');
    setCover(coverFor(user));
  }, [user]);

  if (!user) return null;
  const saved = { name: user.name, title: user.title ?? '', bio: user.bio ?? '', cover: coverFor(user) };
  const dirty = name !== saved.name || title !== saved.title || bio !== saved.bio || cover !== saved.cover;
  // The header shows the draft, so edits are visible before saving.
  const shown = { ...user, name, title, bio, cover };
  const access = (user.permissions ?? []).map((p) => ({ key: p, label: t(`perm.${p}` as MessageKey) })).filter((p) => p.label !== `perm.${p.key}`);

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFieldErrors({});
    try {
      await api.updateMe({ name, title, bio, cover });
      await refresh();
      toast(t('account.updated'));
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setFieldErrors(err.fields);
      else toast(err instanceof ApiError ? err.message : t('account.update_failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  function resetProfile() {
    setName(saved.name);
    setTitle(saved.title);
    setBio(saved.bio);
    setCover(saved.cover);
    setFieldErrors({});
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    setPwFields({});
    try {
      await api.updateMe(pw);
      setPw({ currentPassword: '', newPassword: '' });
      toast(t('account.password_changed'));
    } catch (err) {
      if (err instanceof ApiError) {
        setPwFields(err.fields);
        if (!Object.keys(err.fields).length) toast(err.message, 'error');
      }
    }
  }

  function pickPhoto(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setCropping(file);
  }

  async function uploadPhoto(blob: Blob) {
    setCropping(null);
    setPhotoBusy(true);
    try {
      await api.uploadAvatar(blob);
      await refresh();
      toast(t('account.photo_updated'));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('account.photo_failed'), 'error');
    } finally {
      setPhotoBusy(false);
    }
  }

  async function removePhoto() {
    setPhotoBusy(true);
    try {
      await api.deleteAvatar();
      await refresh();
      toast(t('account.photo_removed'));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('account.photo_failed'), 'error');
    } finally {
      setPhotoBusy(false);
    }
  }

  return (
    <>
      <Topbar title={t('account.title')} />
      {user.role === 'respondent' && <div className="banner mb-16">{t('account.respondent_note')}</div>}

      <section className="card profile-hero" aria-label={t('account.profile')}>
        <div className="profile-cover" data-cover={cover} aria-hidden />
        <div className="profile-hero-body">
          <div className="profile-avatar">
            <Avatar user={shown} size={112} ring />
            <button type="button" className="profile-avatar-edit" onClick={() => fileInput.current?.click()} disabled={photoBusy} aria-label={t('account.photo_change')} title={t('account.photo_change')}>
              <Camera />
            </button>
          </div>
          <div className="profile-id">
            <h2 className="profile-name">{shown.name || shown.email}</h2>
            {shown.title && <div className="profile-title">{shown.title}</div>}
            <div className="profile-meta">
              <span className="pill green">{roleLabel(t, user.role, user.roleName)}</span>
              <span className="profile-meta-item">
                <Mail /> {user.email}
              </span>
              <span className="profile-meta-item">
                <CalendarDays /> {t('account.member_since', { date: formatDate(user.createdAt) })}
              </span>
            </div>
            {shown.bio && <p className="profile-bio">{shown.bio}</p>}
          </div>
        </div>
      </section>

      <div className="profile-grid">
        <form className="card" onSubmit={saveProfile}>
          <div className="card-head">
            <div className="tile">
              <UserRound />
            </div>
            <div className="grow">
              <h2>{t('account.profile')}</h2>
              <div className="sub">{t('account.profile_hint')}</div>
            </div>
          </div>
          <div className="card-pad form-grid" style={{ paddingTop: 0 }}>
            <div className="photo-row">
              <Avatar user={shown} size={56} />
              <div className="grow">
                <div className="label-text">{t('account.photo')}</div>
                <div className="muted">{t('account.photo_hint')}</div>
              </div>
              <div className="row gap-8">
                <button type="button" className="btn sm" onClick={() => fileInput.current?.click()} disabled={photoBusy}>
                  {user.avatarUrl ? t('account.photo_change') : t('account.photo_upload')}
                </button>
                {user.avatarUrl && (
                  <button type="button" className="btn ghost sm icon" onClick={() => void removePhoto()} disabled={photoBusy} aria-label={t('account.photo_remove')} title={t('account.photo_remove')}>
                    <Trash2 />
                  </button>
                )}
              </div>
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pickPhoto} />
            </div>

            <Field label={t('account.display_name')} error={fieldErrors.name} htmlFor="profile-name">
              <input id="profile-name" className="input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </Field>
            <Field label={t('account.job_title')} error={fieldErrors.title} htmlFor="profile-title">
              <input id="profile-title" className="input" value={title} maxLength={TITLE_MAX} placeholder={t('account.job_title_placeholder')} onChange={(e) => setTitle(e.target.value)} autoComplete="organization-title" />
            </Field>
            <Field label={t('account.bio')} error={fieldErrors.bio} hint={`${bio.length}/${BIO_MAX}`} htmlFor="profile-bio">
              <textarea id="profile-bio" className="textarea" rows={3} value={bio} maxLength={BIO_MAX} placeholder={t('account.bio_placeholder')} onChange={(e) => setBio(e.target.value)} />
            </Field>
            <div>
              <div className="label-text">{t('account.banner')}</div>
              <div className="cover-picker" role="radiogroup" aria-label={t('account.banner')}>
                {COVERS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={cover === c}
                    aria-label={t(`account.banner.${c}` as MessageKey)}
                    title={t(`account.banner.${c}` as MessageKey)}
                    className={`cover-swatch${cover === c ? ' on' : ''}`}
                    data-cover={c}
                    onClick={() => setCover(c)}
                  />
                ))}
              </div>
            </div>
            <div className="row gap-8 center">
              <button className="btn primary" disabled={!dirty || saving}>
                {t('common.save')}
              </button>
              {dirty && (
                <>
                  <button type="button" className="btn" onClick={resetProfile} disabled={saving}>
                    {t('account.reset')}
                  </button>
                  <span className="muted">{t('account.unsaved')}</span>
                </>
              )}
            </div>
          </div>
        </form>

        <div className="profile-side">
          {access.length > 0 && (
            <div className="card">
              <div className="card-head">
                <div className="tile">
                  <ShieldCheck />
                </div>
                <div className="grow">
                  <h2>{t('account.access')}</h2>
                  <div className="sub">{t('account.access_hint')}</div>
                </div>
              </div>
              <div className="card-pad access-chips" style={{ paddingTop: 0 }}>
                {access.slice(0, ACCESS_SHOWN).map((p) => (
                  <span key={p.key} className="pill">
                    {p.label}
                  </span>
                ))}
                {access.length > ACCESS_SHOWN && <span className="pill blue">{t('account.access_more', { count: access.length - ACCESS_SHOWN })}</span>}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head">
              <div className="tile">
                <ZoomIn />
              </div>
              <div className="grow">
                <h2>{t('account.display')}</h2>
                <div className="sub">{t('account.display_hint')}</div>
              </div>
            </div>
            <div className="card-pad stack gap-16" style={{ paddingTop: 0 }}>
              <div>
                <div className="label-text">{t('account.zoom')}</div>
                <div className="seg wrap" role="radiogroup" aria-label={t('account.zoom')}>
                  {ZOOM_LEVELS.map((z) => (
                    <button key={z} type="button" role="radio" aria-checked={zoom === z} className={zoom === z ? 'active' : ''} onClick={() => setZoom(z)}>
                      {Math.round(z * 100)}%
                    </button>
                  ))}
                </div>
                <p className="hint mt-8">{t('account.zoom_hint')}</p>
              </div>
              <div>
                <div className="label-text">
                  <PanelLeft className="inline-icon" /> {t('account.sidebar')}
                </div>
                <div className="seg" role="radiogroup" aria-label={t('account.sidebar')}>
                  <button type="button" role="radio" aria-checked={!rail} className={!rail ? 'active' : ''} onClick={() => setRail(false)}>
                    {t('account.sidebar_expanded')}
                  </button>
                  <button type="button" role="radio" aria-checked={rail} className={rail ? 'active' : ''} onClick={() => setRail(true)}>
                    {t('account.sidebar_collapsed')}
                  </button>
                </div>
                <p className="hint mt-8">{t('account.sidebar_hint')}</p>
              </div>
              <div>
                <div className="label-text">{t('account.accent')}</div>
                <AccentPicker />
              </div>
            </div>
          </div>

          <form className="card" onSubmit={savePassword}>
            <div className="card-head">
              <div className="tile">
                <KeyRound />
              </div>
              <h2 className="grow">{t('account.password')}</h2>
            </div>
            <div className="card-pad form-grid" style={{ paddingTop: 0 }}>
              <Field label={t('account.current_password')} error={pwFields.currentPassword}>
                <PasswordInput autoComplete="current-password" required value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
              </Field>
              <Field label={t('account.new_password')} error={pwFields.newPassword} hint={t('auth.password_hint')}>
                <PasswordInput autoComplete="new-password" required minLength={8} value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
              </Field>
              <div>
                <button className="btn primary">{t('account.change_password')}</button>
              </div>
            </div>
          </form>

          <div className="card">
            <div className="card-head">
              <div className="tile">
                <Languages />
              </div>
              <div className="grow">
                <h2>{t('account.language')}</h2>
                <div className="sub">{t('account.language_hint')}</div>
              </div>
            </div>
            <div className="card-pad" style={{ paddingTop: 0 }}>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {cropping && <AvatarCropper file={cropping} onCancel={() => setCropping(null)} onDone={(blob) => void uploadPhoto(blob)} />}
    </>
  );
}
